import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fullscreen?: boolean;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
  errorMessage: string;
}

export class RouteErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    isChunkError: false,
    errorMessage: '',
  };

  public static getDerivedStateFromError(error: Error): State {
    const message = error?.message || String(error || '');
    const isChunkError =
      error?.name === 'ChunkLoadError' ||
      /Loading chunk/i.test(message) ||
      /MIME type/i.test(message) ||
      /is not executable/i.test(message) ||
      /Failed to fetch dynamically imported module/i.test(message) ||
      /error loading dynamically imported module/i.test(message);

    return {
      hasError: true,
      isChunkError,
      errorMessage: message,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('RouteErrorBoundary caught an error:', error, errorInfo);
  }

  private handleReload = () => {
    // Clear the reload-attempt flags so fresh chunk loads are attempted
    try {
      sessionStorage.removeItem('chunk_reload_attempts');
      sessionStorage.removeItem('chunk_reload_ts');
    } catch {
      // Ignore storage access errors
    }
    // Hard navigation with cache-buster timestamp to ensure fresh bundle assets
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('_v', Date.now().toString());
      window.location.href = url.toString();
    } catch {
      window.location.reload();
    }
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      const isFullscreen = this.props.fullscreen ?? false;

      return (
        <div
          style={{
            minHeight: isFullscreen ? '100vh' : 'min(70vh, 520px)',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            backgroundColor: isFullscreen ? '#060606' : 'transparent',
            color: '#f8fafc',
            textAlign: 'center',
            fontFamily: 'Inter, system-ui, sans-serif',
            boxSizing: 'border-box',
          }}
        >
          <div
            style={{
              maxWidth: '480px',
              width: '100%',
              padding: '32px 24px',
              borderRadius: '16px',
              backgroundColor: '#111114',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.6)',
            }}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                backgroundColor: 'rgba(59, 130, 246, 0.12)',
                border: '1px solid rgba(59, 130, 246, 0.25)',
                color: '#60a5fa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.4rem',
                margin: '0 auto 16px auto',
              }}
            >
              {this.state.isChunkError ? '🔄' : '⚠️'}
            </div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '8px', color: '#ffffff' }}>
              {this.state.isChunkError ? 'New Version Available' : 'Something went wrong'}
            </h2>
            <p style={{ fontSize: '0.875rem', color: '#94a3b8', lineHeight: 1.5, marginBottom: '24px' }}>
              {this.state.isChunkError
                ? 'A new build has been deployed. Please reload the page to load the latest version.'
                : 'An unexpected error occurred while loading this page. Please try reloading.'}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={this.handleReload}
                style={{
                  padding: '10px 20px',
                  borderRadius: '10px',
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
              >
                Reload Page
              </button>
              <button
                type="button"
                onClick={this.handleGoHome}
                style={{
                  padding: '10px 20px',
                  borderRadius: '10px',
                  backgroundColor: 'transparent',
                  color: '#94a3b8',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                }}
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

